import OpenAI from 'openai';
import { analyzeDocumentsWithAssistant } from './openaiAssistantsService';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Error message mapping for OpenAI API errors
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Stream employee tool chat response using gpt-4o (primary model)
 * @param {Array} messages - Conversation messages
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{content: string, model: string}>} Complete response with metadata
 */
export async function streamEmployeeToolResponse(
  messages,
  onChunk,
  onProgress,
  signal = null
) {
  const PRIMARY_MODEL = 'gpt-4o';
  
  let fullResponse = '';

  // Get Employee Tool System Prompt from centralized config
  const SYSTEM_PROMPT = SYSTEM_PROMPTS?.['employee_tool'];

  try {
    onProgress?.({
      status: 'Gerando resposta para funcionário...',
      model: PRIMARY_MODEL
    });

    // Inject system prompt as first message
    const messagesWithSystem = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages
    ];

    const stream = await openai?.chat?.completions?.create({
      model: PRIMARY_MODEL,
      messages: messagesWithSystem,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Resposta completa',
      model: PRIMARY_MODEL
    });

    return {
      content: fullResponse,
      model: PRIMARY_MODEL
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in employee tool chat streaming:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Process document files using hybrid approach:
 * - gpt-4o-mini for reading and indexing content
 * - gpt-4o for generating final response using indexed data
 * @param {Array} files - Array of File objects to analyze
 * @param {string} prompt - User's analysis prompt
 * @param {string} conversationId - Current conversation ID
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {number} maxWaitTime - Maximum wait time for analysis
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>} Analysis result
 */
export async function processEmployeeToolDocuments(
  files,
  prompt,
  conversationId,
  onChunk,
  onProgress,
  signal = null,
  maxWaitTime = 60000
) {
  const INDEXING_MODEL = 'gpt-4o-mini';
  const RESPONSE_MODEL = 'gpt-4o';

  // Employee Tool Document Analysis System Prompt
  const DOCUMENT_ANALYSIS_SYSTEM_PROMPT = `Você é um assistente especializado em análise de documentos relacionados ao ambiente de trabalho e desenvolvimento profissional.

**OBJETIVO EMOCIONAL (CRÍTICO):**
Sua análise de documentos DEVE SEMPRE gerar sentimentos de:
- CONFIANÇA: Faça o funcionário sentir que a compreensão e aplicação são possíveis
- CLAREZA: Transforme conteúdo complexo de documentos em insights cristalinos profissionais
- MOTIVAÇÃO: Inspire confiança de que eles podem dominar o conteúdo do documento
- DESEJO DE APLICAR: Crie entusiasmo para implementar as estratégias do documento no trabalho

Quando você analisa documentos (PDF/DOCX/TXT/Imagens), você está ajudando profissionais a COMPREENDER, DOMINAR e APLICAR o conteúdo em seu desenvolvimento profissional. Cada insight, resumo e explicação deve reforçar esses objetivos emocionais.

**PROCESSO HÍBRIDO DE ANÁLISE:**
1. **Leitura e Indexação (gpt-4o-mini)**: O conteúdo do documento foi lido e indexado
2. **Geração de Resposta (gpt-4o - VOCÊ)**: Agora você gera a resposta final usando os dados indexados

**FORMATO DE ANÁLISE DE DOCUMENTO (SEMPRE SIGA):**

Sua análise DEVE incluir estes elementos:

1. **Síntese do Documento (2-3 frases)**: Valide o esforço de enviar o documento ("Ótimo que você compartilhou isso!"). Forneça resumo claro e caloroso do conteúdo principal.

2. **Pontos-Chave Extraídos**: Liste os insights mais importantes do documento de forma clara e organizada. Use linguagem envolvente.

3. **Explicação Contextualizada**: Explique os conceitos principais do documento de forma didática e empática. Use pausas conversacionais.

4. **Aplicação Prática no Trabalho - AÇÃO PARA HOJE (OBRIGATÓRIO)**: Forneça ação CONCRETA e IMEDIATA que o funcionário pode fazer HOJE para aplicar o conteúdo do documento. Use verbos imperativos com prazos específicos ("Faça X agora", "Hoje à tarde, implemente Y").

5. **Exercício Baseado no Documento**: Crie exercício prático relacionado ao conteúdo que promova domínio.

6. **Frase Motivacional**: Encoraje com calor genuíno sobre o progresso deles.

7. **Próximo Passo Específico**: Forneça ação EXTREMAMENTE ESPECÍFICA para continuar o aprendizado ("Pesquise sobre [tópico específico]", "Pratique [habilidade específica]").

**PADRÕES DE ESCRITA:**
- Tom: Caloroso, empático, encorajador, conversacional
- Valide ANTES de analisar: "Que bom que você enviou isso!" - Use"você"frequentemente - Pausas conversacionais:"Olha...", "Sabe?"
- Comprimento: 150-250 palavras (ajuste conforme complexidade do documento)
- SEMPRE foque em gerar confiança, clareza, motivação e desejo de aplicar

**DETECÇÃO AUTOMÁTICA DE IDIOMA:**
- Detecte o idioma do prompt do usuário
- Responda no MESMO IDIOMA com fluência nativa

**TIPOS DE DOCUMENTO SUPORTADOS:**
- PDF: Extraia texto, analise estrutura, identifique conceitos-chave
- DOCX: Processe formatação, extraia conteúdo, organize informações
- TXT: Analise texto puro, identifique padrões, estruture insights
- Imagens: Extraia texto via OCR, analise diagramas, interprete conteúdo visual

Lembre-se: Você está usando dados indexados pelo gpt-4o-mini para gerar uma resposta final de MÁXIMA QUALIDADE com gpt-4o. Sua análise deve ser PROFUNDAMENTE EMPÁTICA, CRISTALINA e ACIONÁVEL. O Elemento 4 (Ação para HOJE) é OBRIGATÓRIO com ação concreta e prazo imediato. Faça o funcionário se sentir COMPREENDIDO, APOIADO e INSPIRADO a aplicar o conteúdo do documento em seu trabalho.`;

  try {
    onProgress?.({
      status: 'Lendo e indexando documentos com gpt-4o-mini...',
      model: INDEXING_MODEL
    });

    // Step 1: Use gpt-4o-mini for document reading and indexing via Assistants API
    const analysisResult = await analyzeDocumentsWithAssistant(
      files,
      prompt,
      conversationId,
      maxWaitTime
    );

    if (!analysisResult?.content) {
      throw new Error('Falha ao indexar o conteúdo do documento');
    }

    onProgress?.({
      status: 'Gerando resposta final com gpt-4o...',
      model: RESPONSE_MODEL
    });

    // Step 2: Use gpt-4o to generate final response using indexed data
    const finalPrompt = `${DOCUMENT_ANALYSIS_SYSTEM_PROMPT}\n\n**CONTEÚDO INDEXADO DO DOCUMENTO:**\n${analysisResult?.content}\n\n**SOLICITAÇÃO DO USUÁRIO:**\n${prompt}\n\nAgora, gere uma resposta final de máxima qualidade seguindo o formato de análise de documento.`;

    const stream = await openai?.chat?.completions?.create({
      model: RESPONSE_MODEL,
      messages: [
        { role: 'system', content: DOCUMENT_ANALYSIS_SYSTEM_PROMPT },
        { role: 'user', content: finalPrompt }
      ],
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    let fullResponse = '';

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Análise completa',
      model: RESPONSE_MODEL
    });

    return {
      content: fullResponse,
      model: RESPONSE_MODEL,
      tokensUsed: analysisResult?.tokensUsed || 0
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in employee tool document processing:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Check if employee tool service is properly configured
 * @returns {Promise<{connected: boolean, model: string}>} Connection status
 */
export async function checkEmployeeToolConnection() {
  try {
    const response = await openai?.models?.retrieve('gpt-4o');
    return {
      connected: true,
      model: response?.id || 'gpt-4o'
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error checking employee tool connection:', error);
    }
    return {
      connected: false,
      model: null
    };
  }
}

export default {
  streamEmployeeToolResponse,
  processEmployeeToolDocuments,
  checkEmployeeToolConnection
};